import { T_MongoId } from "../types/_utils.types";
import { CreditorDto } from "./creditor.dto";
import { FileDto } from "./file.dto";
import { LoanDto } from "./loan.dto";
import { RoleTypes, UserSchemaDto } from "./user.dto";

export enum ProfileType {
  DEFAULT = "default",
  GOOGLE = "google",
  MICROSOFT = "microsoft",
  YAHOO = "yahoo",
  APPLE = "apple",
  SMTP = "smtp_password",
}

export enum AuthType {
  OAUTH = "oauth",
  APP_PASSWORD = "app_password",
  SMTP_PASSWORD = "smtp_password",
  WORKSPACE = "workspace",
  SERVICE_ACCOUNT = "service_account",
}

export enum ProfileAclCapabilities {
  READ = "read",
  EDIT = "edit",
  ADMIN = "admin",
}

export type ProfileAclTargets = {
  roles: RoleTypes[];
  users: Pick<UserSchemaDto, "_id" | "name" | "email" | "role">[];
};

export type ProfileACL = Record<ProfileAclCapabilities, ProfileAclTargets>;

export const DEFAULT_PROFILE_ACL: ProfileACL = {
  [ProfileAclCapabilities.ADMIN]: {
    roles: [RoleTypes.SUPERADMIN],
    users: [],
  },
  [ProfileAclCapabilities.EDIT]: {
    roles: [],
    users: [],
  },
  [ProfileAclCapabilities.READ]: {
    roles: [],
    users: [],
  },
};

export type ProfileDto = {
  email: string;
  emailVerified: boolean;
  user: string;
  type: ProfileType;
  isActive: boolean;
  isLastUsed: boolean;

  displayName?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  avatarFile?: string;

  acl: ProfileACL;
};

export enum DeliveryProvider {
  SMTP = "SMTP",
  GOOGLE = "GOOGLE",
  MICROSOFT = "MICROSOFT",
  YAHOO = "YAHOO",
  APPLE = "APPLE",
  OTHER = "OTHER",
}
export interface Email {
  id: string;
  sender: string;
  email: string;
  subject: string;
  preview: string;
  time: string;
  avatar: string;
  isRead: boolean;
  chain: EmailMessage[];
}

export interface EmailMessage {
  id: string;
  sender: string;
  email: string;
  content: string;
  time: string;
  avatar: string;
  attachments: Attachment[];
}

export interface Attachment {
  name: string;
  size: string;
  type: string;
  url: string;
}

export type MessageDto = {
  subject: string;
  text: string;
  html: string;
  attachments?: Attachment[];

  thread: string;
  parent?: string;

  // email address of sender
  sender: string;
  from: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  participants: {
    id: string;
    seenAt: Date;
  }[];

  deliveryProvider: DeliveryProvider;
  deliveredAt?: Date;

  externalThreadId?: string;
  /** optional Message-Id value, random value will be generated if not set */
  messageId?: string;
  /** An e-mail address that will appear on the Reply-To: field */
  replyTo?: string;
  /** The message-id this message is replying */
  inReplyTo?: string;
  /** Message-id list (an array or space separated string) */
  references?: string[];
};

type OAuthResponse = {
  email: string;
  isActive: boolean;
  type: ProfileType;
  applications: T_MongoId;
  emailVerified: boolean;
  isLastUsed: boolean;
  displayName?: string | undefined;
  firstName?: string | undefined;
  lastName?: string | undefined;
  avatarUrl?: string | undefined;
  avatarFile?: FileDto;
  lastLoginAt?: Date | undefined;
  lastSyncAt?: Date | undefined;
  _id: T_MongoId;
};

export interface EmailThread {
  _id: string;
  application: T_MongoId;

  loanId?: string;
  subject: string;
  snippet: string;
  profile: Profile;
  participants: Participant[];
  from: string[]; // everyone who sent messages in this thread
  to: string[]; // primary recipients
  cc: string[];
  bcc: string[];
  internalTags?: string[];
  creditor?: CreditorDto[];
  loan?: LoanDto[];
  displayTags?: string[];
  lastMessagedAt?: Date;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
  lastMessage?: LastMessage;
  lastSentBy: string;
  lastSentByEmail: string;
  lastSentByName: string;
  hasRead: boolean;
  externalId: string;
}

export interface Profile {
  _id: string;
  applications: string[];
  type: string;
  createdAt: string;
  emailVerified: boolean;
  isActive: boolean;
  isLastUsed: boolean;
  updatedAt: string;
  email: string;
}

export interface Participant {
  mailAddress: string;
  _id: string;
}

export interface LastMessage {
  _id: string;
  subject: string;
  text: string;
  html: string;
  attachments: Attachment[];
  thread: string;
  parent: string;
  sender: string;
  from: string;
  to: string[];
  cc: string[];
  bcc: string[];
  participants: LastMessageParticipant[];
  deliveryProvider: string;
  inReplyTo: string;

  references: string[];
  createdAt: Date;
  updatedAt: Date;
  deliveredAt: string;
  messageId: string;
}

export interface LastMessageParticipant {
  mail: string;
  _id: string;
}

export interface EmailThreadData {
  _id: string;
  subject: string;
  text: string;
  html: string;
  attachments: Attachment[]; // You can replace `any` with a proper attachment type if needed
  thread: EmailThread;
  application?: T_MongoId;
  loanId?: T_MongoId;

  parent?: string | null;

  sender: string;
  from: string;
  to: string[];
  cc: string[];
  bcc: string[];

  participants: LastMessageParticipant[];

  deliveryProvider: DeliveryProvider;

  inReplyTo?: string | null;
  references: string[];

  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  deliveredAt: string; // ISO date string

  messageId: string;
}

export interface EmailAccountConfig {
  email: string;
  emailVerified: boolean;
  displayName?: string;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
  isActive?: boolean;
  type: ProfileType;
  authType: AuthType;
  oauthEnabled?: boolean;
  appPassEnabled?: boolean;
  smtpPassEnabled?: boolean;
  appPassword?: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpUsername?: string;

  smtpPassword?: string;
  smtpTls?: boolean;
  imapHost?: string;
  imapPort?: number;
  imapUsername?: string;
  imapPassword?: string;
  imapTls?: boolean;
  lastSyncAt: string | null;
  isSyncing: boolean;
  _id: string;
  selected: boolean;
  threadsCount: number;
  messagesCount: number;
  isDefaultSender: boolean;

  acl?: ProfileACL;
}

export enum SystemEmailStaticTags {
  CREDITOR_COMMUNICATION = "creditor_communication",
  DEBTOR_COMMUNICATION = "debtor_communication",
}

export enum SystemTagField {
  /** Mongo ID of application */
  APP = "app_id",
  APP_KEY = "app_key",
  /** Mongo ID of organization */
  ORG = "org_id",
  ORG_CODE = "org_code",
  /** External ID of loan (from GetClear) */
  LOAN = "loan_id",
  /** Mongo ID of profile */
  PROFILE = "profile_id",
  /** Mongo ID of creditor */
  CREDITOR = "creditor_id",
  /** Email address of users that have sent message to this thread */
  FROM = "from_addr",
  /** Email address of users that are added as primary recipient in this thread */
  TO = "to_addr",
  /** Email address of users that are added as cc to this thread */
  CC = "cc_addr",
  /** Email address of users that are added as bcc to this thread */
  BCC = "bcc_addr",
}

export interface Creditor_Filter {
  _id: string;
  name: string;
  slug: string;
  allowed_emails: string[];
  emails: string[];
  threads: number;
  tag: string;
}

export interface DebtorFilter {
  _id: string;
  app_key: string;
  meta: {
    client_name: string;
    business_name: string;
    email: string;
    phone: string;
    address: string;
  };
  emails: string[];
  threads: number;
  tag: string;
}

export type DraftDto = {
  _id: T_MongoId;
  thread: T_MongoId;
  message: T_MongoId;
  profile: T_MongoId;
  subject: string;
  replyMessage: string;
  to: string[];
  cc: string[];
  bcc: string[];
  createdAt: Date;
  updatedAt: Date;
};
